import React from 'react';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { useHistoricoSinaisVitais } from '../../hooks/data/useHistoricoSinaisVitais';
import LoadingIndicator from '../Common/LoadingIndicator';
import Alert from '../UI/Alert';
import Title from '../Layout/Title'; // Reutilizamos o componente Title

/**
 * @component GraficoLinhaSinaisVitais
 * @description Exibe a tendência dos Sinais Vitais (Temperatura, Pressão, SatO2) nos últimos 30 dias.
 * @param {number} pacienteId - O ID do paciente para buscar o histórico.
 */
const GraficoLinhaSinaisVitais = ({ pacienteId }) => {
    // 1. Uso do Hook para obter os dados
    const { historicoSV, isLoading, error } = useHistoricoSinaisVitais(pacienteId);

    // 2. Estados de Carregamento e Erro
    if (isLoading) {
        return <LoadingIndicator message="Carregando tendência de Sinais Vitais..." />;
    }

    if (error) {
        return <Alert type="error" message={error} />;
    }

    if (historicoSV.length === 0) {
        return (
            <div className="py-8">
                <Alert type="info" message="Não há registros de Sinais Vitais nos últimos 30 dias para exibir o gráfico de tendência." />
            </div>
        );
    }
    
    // 3. Formatação e Mapeamento dos Dados
    const formattedData = historicoSV.map(item => ({
        // Usamos o timestamp original para a organização no eixo X
        time: new Date(item.data_registro).getTime(), 
        // Renomear campos para visualização clara no gráfico
        Temperatura: item.temperatura,
        'P. Sistólica': item.pressao_sistolica,
        'P. Diastólica': item.pressao_diastolica,
        'Sat. O2': item.saturacao_o2,
    }));

    // 4. Configuração das Linhas com cores mais suaves e profissionais
    const lines = [
        { key: 'Temperatura', color: '#EF4444', unit: '°C', strokeWidth: 2.5 },
        { key: 'P. Sistólica', color: '#3B82F6', unit: 'mmHg', strokeWidth: 2 },
        { key: 'P. Diastólica', color: '#60A5FA', unit: 'mmHg', strokeWidth: 2 },
        { key: 'Sat. O2', color: '#10B981', unit: '%', strokeWidth: 2.5 },
    ];

    // 5. Componente Tooltip Customizado para Recharts (melhorado)
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-4 border border-gray-200 shadow-xl rounded-lg text-sm min-w-[200px]">
                    <p className="font-semibold text-gray-800 mb-2 pb-2 border-b border-gray-200">
                        {new Date(label).toLocaleDateString('pt-BR', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                        })} 
                        {' '}
                        {new Date(label).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <div className="space-y-1.5">
                        {payload.map((item, index) => {
                            const lineConfig = lines.find(l => l.key === item.name);
                            return (
                                <div key={index} className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <div 
                                            className="w-3 h-3 rounded-full" 
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span className="font-medium text-gray-700">{item.name}:</span>
                                    </div>
                                    <span className="font-semibold" style={{ color: item.color }}>
                                        {item.value} {lineConfig?.unit || ''}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        return null;
    };


    // 6. Renderização Principal do Gráfico (melhorado)
    return (
        <div className="w-full">
            <ResponsiveContainer width="100%" height={400}>
                <LineChart 
                    data={formattedData} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                    <CartesianGrid 
                        strokeDasharray="3 3" 
                        stroke="#e5e7eb" 
                        opacity={0.5}
                    />
                    
                    {/* Eixo X: Data do Registro (ajustado para 30 dias) */}
                    <XAxis 
                        dataKey="time" 
                        type="number" 
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(timestamp) => {
                            const date = new Date(timestamp);
                            // Mostra data e hora para melhor visualização em 30 dias
                            return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' + 
                                   date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                        }}
                        stroke="#6b7280"
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        style={{ fontSize: '12px' }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                    />
                    
                    {/* Eixo Y: Melhorado com formatação */}
                    <YAxis 
                        stroke="#6b7280"
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        style={{ fontSize: '12px' }}
                    /> 

                    {/* Tooltip personalizado */}
                    <Tooltip 
                        content={<CustomTooltip lines={lines} />}
                        cursor={{ stroke: '#9ca3af', strokeWidth: 1, strokeDasharray: '5 5' }}
                    />
                    
                    {/* Legenda melhorada */}
                    <Legend 
                        iconType="line" 
                        wrapperStyle={{ paddingTop: '20px', paddingBottom: '10px' }}
                        iconSize={16}
                        formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
                    />
                    
                    {/* Renderização das Linhas (melhoradas) */}
                    {lines.map(line => (
                        <Line 
                            key={line.key} 
                            type="monotone"
                            dataKey={line.key} 
                            stroke={line.color} 
                            dot={{ r: 3, fill: line.color, strokeWidth: 2, stroke: '#fff' }}
                            activeDot={{ r: 5, fill: line.color, strokeWidth: 2, stroke: '#fff' }}
                            strokeWidth={line.strokeWidth || 2}
                            name={line.key}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default GraficoLinhaSinaisVitais;